import InsightsApi from '@/api/insights.api'

export const SLOTS_PER_DAY = 48
export const TOTAL_SLOTS = 336
export const PRE_PEAK_THRESHOLD = 0.7
export const PEAK_THRESHOLD = 0.9
export const SELLER_OFFER_INSIGHT_TTL = 10 * 60 * 1000
export const CAMPAIGNS_SPEND_TTL = 10 * 60 * 1000

/**
 * Compute spend-vs-budget stats for a 7-day spend array.
 * @param {number[]} weeklySpend - 7-element array (Sun-Sat) of dollar amounts
 * @param {number} dailyBudget - daily budget in USD
 * @returns {{ spendBudgetPercent: number, spendBudgetZero: boolean, weeklySpendData: object } | null}
 */
export function computeSpendBudget(weeklySpend, dailyBudget) {
  if (!weeklySpend || weeklySpend.length !== 7 || !dailyBudget) {
    return null
  }

  const days = weeklySpend.map(spent => {
    if (spent === 0) {
      return { dollar: 0, percent: 0, zero: true }
    }

    let p = (spent / dailyBudget) * 100
    if (p < 0) {
      p = p + 100
    }

    return { dollar: Math.round(spent), percent: Math.round(p), zero: false }
  })

  const nonZeroDays = days.filter(d => !d.zero)
  const allZero = nonZeroDays.length === 0
  const avg = allZero ? 0 : Math.round(nonZeroDays.reduce((sum, d) => sum + d.percent, 0) / nonZeroDays.length)

  return {
    spendBudgetPercent: avg,
    spendBudgetZero: allZero,
    weeklySpendData: { dailyBudget, days }
  }
}

const state = {
  sellerOfferInsight: null,
  sellerOfferInsightFetchedAt: null,
  revenueChart: {
    dataPoints: [],
    summary: null,
    loading: false,
    error: null
  },
  campaignChart: {
    dataPoints: [],
    loading: false,
    error: null
  },
  campaignSpend: [],
  campaignSpendFetchedAt: null,
  campaignSpendLoading: false,
  campaignSpendError: null
}

const getters = {
  revenueChartData: (state) => state.revenueChart.dataPoints,
  revenueChartSummary: (state) => state.revenueChart.summary,
  isRevenueChartLoading: (state) => state.revenueChart.loading,
  campaignChartData: (state) => state.campaignChart.dataPoints,
  isCampaignChartLoading: (state) => state.campaignChart.loading
}

const actions = {
  async fetchSellerOfferInsights({ commit, state }, sellerOfferId) {
    if (
      state.sellerOfferInsight?.id === sellerOfferId
      && state.sellerOfferInsightFetchedAt
      && Date.now() - state.sellerOfferInsightFetchedAt < SELLER_OFFER_INSIGHT_TTL
    ) {
      return state.sellerOfferInsight
    }

    const response = await InsightsApi.getSellerOfferInsights(sellerOfferId)
    commit('SET_SELLER_OFFER_INSIGHT', response.data || null)
    return response.data
  },

  async fetchRevenueChart({ commit }, { granularityColumn = 'datesByYearMonth' } = {}) {
    commit('SET_REVENUE_CHART_LOADING', true)
    commit('SET_REVENUE_CHART_ERROR', null)

    try {
      const response = await InsightsApi.getRevenueChart({
        granularityColumn
      })

      commit('SET_REVENUE_CHART_DATA', response.data)
      return response.data
    } catch (error) {
      commit('SET_REVENUE_CHART_ERROR', error.message || 'Failed to fetch revenue chart data')
      throw error
    } finally {
      commit('SET_REVENUE_CHART_LOADING', false)
    }
  },

  async fetchCategoryRPC({ commit }, { startDate = null, endDate = null, accountId = null, categoryName = null } = {}) {
    commit('SET_CAMPAIGN_CHART_LOADING', true)
    commit('SET_CAMPAIGN_CHART_ERROR', null)

    try {
      const response = await InsightsApi.getRpcByCategoryChart({
        startDate,
        endDate,
        accountId,
        categoryName
      })

      commit('SET_CAMPAIGN_CHART_DATA', response.data)
      return response.data
    } catch (error) {
      commit('SET_CAMPAIGN_CHART_ERROR', error.message || 'Failed to fetch category data')
      throw error
    } finally {
      commit('SET_CAMPAIGN_CHART_LOADING', false)
    }
  },

  async fetchCampaignSpend({ commit, state }) {
    if (
      state.campaignSpend.length
      && state.campaignSpendFetchedAt
      && Date.now() - state.campaignSpendFetchedAt < CAMPAIGNS_SPEND_TTL
    ) {
      return state.campaignSpend
    }

    commit('SET_CAMPAIGN_SPEND_LOADING', true)
    commit('SET_CAMPAIGN_SPEND_ERROR', null)

    try {
      const response = await InsightsApi.getCampaignSpendInsights()
      commit('SET_CAMPAIGN_SPEND', response.data || [])
      return response.data
    } catch (error) {
      commit('SET_CAMPAIGN_SPEND_ERROR', error.message || 'Failed to fetch campaign spend data')
      throw error
    } finally {
      commit('SET_CAMPAIGN_SPEND_LOADING', false)
    }
  },

  reset({ commit }) {
    commit('RESET_INSIGHTS')
  }
}

const mutations = {
  SET_SELLER_OFFER_INSIGHT(state, data) {
    state.sellerOfferInsight = data
    state.sellerOfferInsightFetchedAt = data ? Date.now() : null
  },
  SET_REVENUE_CHART_DATA(state, data) {
    // Data is raw Ringba API response: { report: { records: [...], partialResult: bool } }
    state.revenueChart.dataPoints = data?.report?.records || []
    state.revenueChart.summary = null // Frontend calculates summary if needed
  },
  SET_REVENUE_CHART_LOADING(state, loading) {
    state.revenueChart.loading = loading
  },
  SET_REVENUE_CHART_ERROR(state, error) {
    state.revenueChart.error = error
  },
  SET_CAMPAIGN_CHART_DATA(state, data) {
    state.campaignChart.dataPoints = data?.report?.records || []
  },
  SET_CAMPAIGN_CHART_LOADING(state, loading) {
    state.campaignChart.loading = loading
  },
  SET_CAMPAIGN_CHART_ERROR(state, error) {
    state.campaignChart.error = error
  },
  SET_CAMPAIGN_SPEND(state, data) {
    state.campaignSpend = data
    state.campaignSpendFetchedAt = data?.length ? Date.now() : null
  },
  SET_CAMPAIGN_SPEND_LOADING(state, loading) {
    state.campaignSpendLoading = loading
  },
  SET_CAMPAIGN_SPEND_ERROR(state, error) {
    state.campaignSpendError = error
  },
  RESET_INSIGHTS(state) {
    state.sellerOfferInsight = null
    state.sellerOfferInsightFetchedAt = null
    state.revenueChart = {
      dataPoints: [],
      loading: false,
      error: null
    }
    state.campaignChart = {
      dataPoints: [],
      loading: false,
      error: null
    }
    state.campaignSpend = []
    state.campaignSpendFetchedAt = null
    state.campaignSpendLoading = false
    state.campaignSpendError = null
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
